import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getFile, getPublicFile } from '@/store/files/actions';

import PropTypes from 'prop-types';

// Глобальный кэш для файлов
const fileCache = new Map();
const loadingCache = new Map();
const subscribers = new Map();
const MAX_CACHE_SIZE = 30;

// Счетчик для оптимизации cleanupCache
let cleanupCounter = 0;
const CLEANUP_INTERVAL = 10; // Очищаем каждые 10 файлов

// Функция очистки старых записей кэша (оптимизированная)
const cleanupCache = () => {
  if (fileCache.size > MAX_CACHE_SIZE) {
    // Используем итератор вместо Array.from для лучшей производительности
    const entries = [];
    for (const [uuid, blobUrl] of fileCache.entries()) {
      entries.push([uuid, blobUrl]);
      if (entries.length >= fileCache.size - MAX_CACHE_SIZE) break;
    }

    entries.forEach(([uuid, blobUrl]) => {
      try {
        // Проверяем валидность blobUrl перед удалением
        if (blobUrl && typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      } catch (error) {
        console.warn('Failed to revoke blob URL:', error);
      }
      fileCache.delete(uuid);
      // НЕ удаляем подписчиков - они могут быть активными
    });
  }
};

// Валидация UUID
const isValidUUID = uuid => {
  return uuid && typeof uuid === 'string' && uuid.trim().length > 0;
};

// Валидация callback'а
const isValidCallback = callback => {
  return callback && typeof callback === 'function';
};

// Кэш для проверки online статуса
let isOnlineCache = null;
let lastOnlineCheck = 0;
const ONLINE_CACHE_DURATION = 1000; // 1 секунда

const checkOnlineStatus = () => {
  const now = Date.now();
  if (isOnlineCache === null || now - lastOnlineCheck > ONLINE_CACHE_DURATION) {
    isOnlineCache = window.navigator.onLine;
    lastOnlineCheck = now;
  }
  return isOnlineCache;
};

const useLocalFileByUUID = (uuid, config = {}) => {
  const { isDefaultNull, defaultImg, publish } = config;
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);
  const updateCallbackRef = useRef(null);

  // Стабилизируем setDefaultImg с useMemo
  const setDefaultImg = useMemo(() => {
    return isDefaultNull ? null : defaultImg;
  }, [isDefaultNull, defaultImg]);

  const [localFile, setLocalFile] = useState(setDefaultImg);
  const [isLoading, setLoading] = useState(true);

  // Проверяем, что компонент все еще смонтирован перед setState
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  // Объединяем setState для лучшей производительности
  const safeSetStates = useCallback(updates => {
    if (isMountedRef.current) {
      updates.forEach(([setter, value]) => setter(value));
    }
  }, []);

  // Создаем стабильные callback'ы без циклических зависимостей
  const onSuccessResponse = useCallback(
    success => {
      if (!isMountedRef.current) return;

      try {
        const imgObjectURL = URL.createObjectURL(success);

        fileCache.set(uuid, imgObjectURL);
        loadingCache.delete(uuid);

        // Оптимизированная очистка кэша
        cleanupCounter++;
        if (cleanupCounter >= CLEANUP_INTERVAL) {
          cleanupCache();
          cleanupCounter = 0;
        }

        if (subscribers.has(uuid)) {
          subscribers.get(uuid).forEach(callback => {
            if (isValidCallback(callback)) {
              try {
                callback(imgObjectURL, false);
              } catch (error) {
                console.warn('Error in subscriber callback:', error);
              }
            }
          });
        }

        // Объединяем setState для одного ререндера
        safeSetStates([
          [setLocalFile, imgObjectURL],
          [setLoading, false],
        ]);
      } catch (error) {
        console.error('Error in onSuccessResponse:', error);
        // Не вызываем onErrorResponse напрямую, а обрабатываем ошибку здесь
        loadingCache.delete(uuid);
        safeSetStates([
          [setLocalFile, setDefaultImg],
          [setLoading, false],
        ]);
      }
    },
    [uuid, setDefaultImg, safeSetStates]
  );

  const onErrorResponse = useCallback(() => {
    if (!isMountedRef.current) return;

    loadingCache.delete(uuid);

    if (subscribers.has(uuid)) {
      subscribers.get(uuid).forEach(callback => {
        if (isValidCallback(callback)) {
          try {
            callback(setDefaultImg, false);
          } catch (error) {
            console.warn('Error in subscriber callback:', error);
          }
        }
      });
    }

    safeSetStates([
      [setLocalFile, setDefaultImg],
      [setLoading, false],
    ]);
  }, [uuid, setDefaultImg, safeSetStates]);

  const loadCurrentFile = useCallback(() => {
    if (loadingCache.has(uuid)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('File is already loading, waiting...');
      }
      return;
    }

    loadingCache.set(uuid, true);

    try {
      if (publish && checkOnlineStatus()) {
        dispatch(getPublicFile(uuid, onSuccessResponse, onErrorResponse));
      } else {
        dispatch(getFile(uuid, onSuccessResponse, onErrorResponse));
      }
    } catch (error) {
      console.error('Error dispatching file request:', error);
      loadingCache.delete(uuid);
      onErrorResponse();
    }
  }, [uuid, publish, dispatch, onSuccessResponse, onErrorResponse]);

  useEffect(() => {
    isMountedRef.current = true;

    if (isValidUUID(uuid) && checkOnlineStatus()) {
      // Проверяем кэш
      if (fileCache.has(uuid)) {
        const cachedFile = fileCache.get(uuid);
        safeSetStates([
          [setLocalFile, cachedFile],
          [setLoading, false],
        ]);
      } else {
        safeSetState(setLoading, true);

        // Подписываемся на изменения этого файла
        if (!subscribers.has(uuid)) {
          subscribers.set(uuid, new Set());
        }

        const updateCallback = (file, loading) => {
          if (isMountedRef.current) {
            safeSetStates([
              [setLocalFile, file],
              [setLoading, loading],
            ]);
          }
        };

        // Сохраняем ссылку на callback
        updateCallbackRef.current = updateCallback;
        subscribers.get(uuid).add(updateCallback);

        // Загружаем файл только если он еще не загружается
        if (!loadingCache.has(uuid)) {
          loadCurrentFile();
        }

        // Отписываемся при размонтировании
        return () => {
          if (subscribers.has(uuid) && updateCallbackRef.current) {
            subscribers.get(uuid).delete(updateCallbackRef.current);
            if (subscribers.get(uuid).size === 0) {
              subscribers.delete(uuid);
            }
          }
        };
      }
    } else {
      safeSetState(setLoading, false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [uuid, safeSetState, safeSetStates, loadCurrentFile]);

  return [localFile, isLoading];
};

useLocalFileByUUID.propTypes = {
  uuid: PropTypes.string,
  options: PropTypes.shape({
    isDefaultNull: PropTypes.bool, // Если 1 то картинка по умолчанию может быть null
    defaultImg: PropTypes.any, // Ссылка  на картинку по умолчанию
    publish: PropTypes.bool, // Если 1 то поиск картинки на сервере через публичный api getPublicFile
  }),
};

export { useLocalFileByUUID };
